import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { FindAppointmentSlotsInput, ListAppointmentsInput } from "@nexxt/contracts/booking";
import { formatDate } from "@nexxt/ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";
import { bookingQueries } from "../booking/booking.queries";

// Server state of the appointments (50-api/booking.md §2.3): the agenda by cursor, one appointment, the slots of a
// showroom day, and the customers an appointment is booked for.

const appointments = api.booking.appointments;

type ListPage = InferResponseType<typeof appointments.$get, 200>;
export type Appointment = ListPage["items"][number];
type Customers = InferResponseType<(typeof api.booking)["appointment-customers"]["$get"], 200>;
export type AppointmentCustomer = Customers["items"][number];

/** The filters of the list input (`listAppointmentsInput`), without the paging the query adds. */
export type AppointmentFilters = Pick<
  ListAppointmentsInput,
  "salesCampaignId" | "brandOrgId" | "customerOrgId" | "status" | "from" | "to"
>;

const keys = {
  all: [...bookingQueries.all, "appointments"] as const,
  /** Prefix of every cached list: the mutations write the appointment they receive into them. */
  lists: [...bookingQueries.all, "appointments", "list"] as const,
  details: [...bookingQueries.all, "appointments", "detail"] as const,
};

export const appointmentQueries = {
  ...keys,

  list: (filters: AppointmentFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          appointments.$get(
            {
              query: { ...filterQuery(filters), ...(pageParam && { cursor: pageParam }), limit: "50" },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  detail: (appointmentId: string) =>
    queryOptions({
      queryKey: [...keys.details, appointmentId],
      queryFn: ({ signal }) =>
        unwrap(appointments[":appointmentId"].$get({ param: { appointmentId } }, { init: { signal } })),
    }),

  /**
   * The slots of a showroom day for the brands named (`findAppointmentSlots`). Asked again every time, never kept:
   * a place can go while the form is open.
   */
  slots: (input: FindAppointmentSlotsInput) =>
    queryOptions({
      queryKey: [...keys.all, "slots", input],
      queryFn: ({ signal }) =>
        unwrap(api.booking["appointment-slots"].find.$post({ json: input }, { init: { signal } })),
      staleTime: 0,
    }),

  /** The customers with `q` in the name, the store or the code, each with its contacts. */
  customers: (q: string) =>
    queryOptions({
      queryKey: [...keys.all, "customers", q],
      queryFn: async ({ signal }) =>
        (await unwrap(api.booking["appointment-customers"].$get({ query: { q } }, { init: { signal } }))).items,
    }),
};

/** Day and time of an appointment, which are the ones of its showroom: "12 Jan 2027 10:30". */
export const appointmentWhen = ({ date, time }: Pick<Appointment, "date" | "time">) => `${formatDate(date)} ${time}`;

/** An appointment by what tells it from the others: who comes and when. */
export const appointmentName = (appointment: Pick<Appointment, "customer" | "date" | "time">) =>
  `${appointment.customer?.name ?? "—"} · ${appointmentWhen(appointment)}`;
